import { SLOT_ATTR } from "./config";
import { SlotController } from "./runtime";
import type { ViewabilityWatcher } from "./viewability";

// Discoverer scans the document for `[data-adpluga-slot]` elements and
// hands each one to a SlotController. A MutationObserver keeps that map
// in sync with SPA navigation that adds/removes nodes at runtime.
//
// Lookup is O(1) via a WeakMap; iteration (for visibilitychange broadcast)
// uses a separate Set kept in step. The WeakMap lets nodes that are
// removed-and-discarded by the host page be GC'd even if destroy() was
// somehow missed.

export interface DiscovererOptions {
  key: string;
  base: string;
  watcher: ViewabilityWatcher;
}

export class Discoverer {
  private readonly key: string;
  private readonly base: string;
  private readonly watcher: ViewabilityWatcher;
  private readonly map = new WeakMap<HTMLElement, SlotController>();
  private readonly live = new Set<SlotController>();
  private readonly mo: MutationObserver;
  private started = false;

  constructor(o: DiscovererOptions) {
    this.key = o.key;
    this.base = o.base;
    this.watcher = o.watcher;
    this.mo = new MutationObserver((muts) => this.onMutations(muts));
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.scanRoot(document);
    this.mo.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  stop(): void {
    if (!this.started) return;
    this.mo.disconnect();
    for (const c of this.live) c.destroy();
    this.live.clear();
    this.started = false;
  }

  // controllerFor returns the controller bound to el (or null). Used by the
  // public AdPluga.refresh()/destroy() API.
  controllerFor(el: HTMLElement): SlotController | null {
    return this.map.get(el) ?? null;
  }

  // broadcast invokes fn on every active controller. Used by the page-wide
  // visibilitychange listener to pause/resume timers.
  broadcast(fn: (c: SlotController) => void): void {
    for (const c of this.live) fn(c);
  }

  private scanRoot(root: Document | Element): void {
    const list = root.querySelectorAll<HTMLElement>("[" + SLOT_ATTR + "]");
    for (let i = 0; i < list.length; i++) {
      const el = list[i];
      if (el) this.mount(el);
    }
  }

  private onMutations(muts: MutationRecord[]): void {
    for (let i = 0; i < muts.length; i++) {
      const m = muts[i];
      if (!m) continue;
      const removed = m.removedNodes;
      for (let j = 0; j < removed.length; j++) {
        const n = removed[j];
        if (n && n.nodeType === Node.ELEMENT_NODE) this.unmountTree(n as Element);
      }
      const added = m.addedNodes;
      for (let j = 0; j < added.length; j++) {
        const n = added[j];
        if (n && n.nodeType === Node.ELEMENT_NODE) this.mountTree(n as Element);
      }
    }
  }

  private mountTree(el: Element): void {
    if (el instanceof HTMLElement && el.hasAttribute(SLOT_ATTR)) {
      this.mount(el);
    }
    const nested = el.querySelectorAll<HTMLElement>("[" + SLOT_ATTR + "]");
    for (let i = 0; i < nested.length; i++) {
      const n = nested[i];
      if (n) this.mount(n);
    }
  }

  private unmountTree(el: Element): void {
    if (el instanceof HTMLElement && this.map.has(el)) this.unmount(el);
    const nested = el.querySelectorAll<HTMLElement>("[" + SLOT_ATTR + "]");
    for (let i = 0; i < nested.length; i++) {
      const n = nested[i];
      if (n && this.map.has(n)) this.unmount(n);
    }
  }

  private mount(el: HTMLElement): void {
    if (this.map.has(el)) return;
    const c = new SlotController({
      el,
      key: this.key,
      base: this.base,
      watcher: this.watcher,
    });
    this.map.set(el, c);
    this.live.add(c);
    c.mount();
  }

  private unmount(el: HTMLElement): void {
    const c = this.map.get(el);
    if (!c) return;
    this.map.delete(el);
    this.live.delete(c);
    c.destroy();
  }
}
